import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { PlusIcon, FileDown } from 'lucide-react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { validationSchema } from '@/pages/GameroomScenario/validation'
import { useAppDispatch } from '@/redux/hooks'
import {
  getRoomDetails,
  getRoomMembers,
  updateRoomDetails,
} from '@/redux/asyncThunks/room'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { toast } from 'sonner'
import GameroomScenarioSettingsSkeleton from './GameroomScenarioSettingsSkeleton'
import { GameRoomStatus, isGameRoomSessionActive } from '@/redux/types/room'
import { EventCard } from './EventCard'
import {
  addSecondsToDateTime,
  convertUTCToLocal,
  getCurrentLocalDateTime,
} from '@/utils/dateTimeUtils'
import RoomNotFound from '../PageNotFound/RoomNotFound'
import { ImportModal } from '@/components/Room/ImportModal'

const DEFAULT_VALUES = {
  scenarioTitle: 'Sample Crisis Scenario',
  eventTitle: 'Emergency Event',
  eventText:
    'A critical situation has developed that requires immediate attention and coordinated response from all team members.',
}

export default function GameroomScenarioSettings() {
  const dispatch = useAppDispatch()
  const params = useParams()
  const roomId = params.roomId as string

  const { currentRoom, isLoading, error } = useSelector(
    (state: RootState) => state.room
  )
  const isGameCompleted = currentRoom?.status === GameRoomStatus.COMPLETED
  const isGameInProgress = isGameRoomSessionActive(currentRoom?.status)
  const isGameStarted = isGameInProgress || isGameCompleted

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [releasingEventId, setReleasingEventId] = useState<number | null>(null)
  const [unreleasingEventId, setUnreleasingEventId] = useState<number | null>(
    null
  )
  const [isUpdatingAutoApprove, setIsUpdatingAutoApprove] = useState<boolean>(false)
  const [
    isUpdatingChatHistorySummarization,
    setIsUpdatingChatHistorySummarization,
  ] = useState(false)
  const [importScenarioOpen, setImportScenarioOpen] = useState<boolean>(false)
  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
      // The audience picker needs the roster; this screen never loaded it.
      dispatch(getRoomMembers(roomId))
    }
  }, [dispatch, roomId])

  if (isLoading) {
    return <GameroomScenarioSettingsSkeleton />
  }

  if (error) {
    return (
      <div className='flex items-center justify-center w-full h-full text-center'>
        <RoomNotFound message='Unable to fetch the details.' />
      </div>
    )
  }

  if (!currentRoom) {
    return <div>No room details found.</div>
  }

  const prepareInitialEvents = () => {
    const currentLocalDateTime = getCurrentLocalDateTime()

    if (!currentRoom.events?.length) {
      return [
        {
          id: Date.now(),
          title: DEFAULT_VALUES.eventTitle,
          text: DEFAULT_VALUES.eventText,
          event_datetime: currentLocalDateTime,
          is_released: false,
          media: [],
        },
      ]
    } else {
      return currentRoom.events.map((event) => {
        let displayDateTime = currentLocalDateTime

        if (event.event_datetime) {
          displayDateTime = convertUTCToLocal(event.event_datetime)
        }

        return {
          ...event,
          id: event.id || Date.now(),
          title: event.title || DEFAULT_VALUES.eventTitle,
          text: event.text || DEFAULT_VALUES.eventText,
          event_datetime: displayDateTime,
          is_released: event.is_released ?? false,
          media: event.media ?? [],
        }
      })
    }
  }

  const handleAutoApproveToggle = async (checked: boolean) => {
    if (isGameCompleted) return

    setIsUpdatingAutoApprove(true)
    try {
      await dispatch(
        updateRoomDetails({
          roomId,
          auto_approve: checked,
        })
      ).unwrap()

      toast.success(
        `Auto-approval ${checked ? 'enabled' : 'disabled'} successfully!`
      )
    } catch (error) {
      toast.error('Failed to update auto-approval setting')
    } finally {
      setIsUpdatingAutoApprove(false)
    }
  }

  const handleChatHistorySummarizationToggle = async (checked: boolean) => {
    if (isGameCompleted) return

    setIsUpdatingChatHistorySummarization(true)
    try {
      await dispatch(
        updateRoomDetails({
          roomId,
          enable_chat_history_summarization: checked,
        })
      ).unwrap()

      toast.success(
        `Chat history summarization ${checked ? 'enabled' : 'disabled'} successfully!`
      )
    } catch (error) {
      toast.error('Failed to update chat history summarization setting')
    } finally {
      setIsUpdatingChatHistorySummarization(false)
    }
  }

  return (
    <TooltipProvider>
      <div className='relative px-24 py-6 space-y-6'>
      <div className='flex flex-wrap gap-x-2 gap-y-2 items-center justify-between'>
        <h1 className='text-2xl font-bold text-dark-blue'>
          Game Room Scenario Settings
        </h1>
        {!isGameCompleted && (
          <Button
            type='button'
            variant='outline'
            className='border-dark-blue text-dark-blue hover:bg-slate-50'
            onClick={() => setImportScenarioOpen(true)}
          >
            <FileDown className='w-4 h-4 mr-2' />
            Import scenario & events
          </Button>
        )}
      </div>

      <p className='text-muted-foreground'>
        {isGameCompleted
          ? 'This game has been completed. The scenario can be viewed but not edited.'
          : 'Configure your game room scenario by adding events dynamically.'}
      </p>

      <div className='inline-flex gap-3 items-center px-4 py-3 bg-gradient-to-r rounded-xl border shadow-sm transition-all duration-200 from-slate-50 to-slate-100 border-slate-200 hover:shadow-md'>
        <div className='flex-1 min-w-0'>
          <Label className='block font-semibold text-slate-800 text-sm mb-0.5 cursor-pointer'>
            Auto-Approval
          </Label>
          <p className='text-xs text-slate-500'>
            Allow players to bypass approval for public actions
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='flex-shrink-0'>
                <Switch
                  checked={currentRoom?.auto_approve ?? false}
                  onCheckedChange={handleAutoApproveToggle}
                  disabled={isGameCompleted || isUpdatingAutoApprove}
                  className='bg-gray-400'
                >
                  <span className='block h-5 w-5 translate-x-[2px] rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out data-[state=checked]:translate-x-[22px]' />
                </Switch>
              </div>
            </TooltipTrigger>
            <TooltipContent className='px-3 py-2 max-w-xs text-sm text-white rounded-md shadow-xl bg-slate-800'>
              <p>
                Enable or disable auto approval for player actions during the
                scenario. When enabled, all player actions will be automatically
                approved without manual review.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className='inline-flex gap-3 items-center px-4 py-3 ml-2 bg-gradient-to-r rounded-xl border shadow-sm transition-all duration-200 from-slate-50 to-slate-100 border-slate-200 hover:shadow-md'>
        <div className='flex-1 min-w-0'>
          <Label className='block font-semibold text-slate-800 text-sm mb-0.5 cursor-pointer'>
            Chat History Summarization
          </Label>
          <p className='text-xs text-slate-500'>
            Automatically summarize older messages when conversations exceed
            history limit
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className='flex-shrink-0'>
                <Switch
                  checked={
                    currentRoom?.enable_chat_history_summarization ?? true
                  }
                  onCheckedChange={handleChatHistorySummarizationToggle}
                  disabled={
                    isGameCompleted || isUpdatingChatHistorySummarization
                  }
                  className='bg-gray-400'
                >
                  <span className='block h-5 w-5 translate-x-[2px] rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out data-[state=checked]:translate-x-[22px]' />
                </Switch>
              </div>
            </TooltipTrigger>
            <TooltipContent className='px-3 py-2 max-w-xs text-sm text-white rounded-md shadow-xl bg-slate-800'>
              <p>
                When enabled, older chat messages are automatically summarized
                to preserve context while staying within token limits. This
                helps maintain conversation continuity in long chats.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <Formik
        initialValues={{
          scenarioTitle:
            currentRoom.scenario_title || DEFAULT_VALUES.scenarioTitle,
          events: prepareInitialEvents(),
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          if (isGameCompleted) return

          setIsSubmitting(true)

          const originalEventIds = new Set(
            currentRoom.events
              ?.map((event) => event.id)
              .filter((id) => id !== undefined) || []
          )

          const eventsWithReleaseStatus = values.events.map((event, index) => {
            const isNewEvent = !event.id || !originalEventIds.has(event.id)

            if (isNewEvent) {
              return {
                ...event,
                is_released: index === 0 ? !isGameStarted : false,
              }
            } else {
              return {
                ...event,
                is_released: event.is_released ?? false,
              }
            }
          })

          const eventsWithSeconds = eventsWithReleaseStatus.map((event) => ({
            id: event.id,
            title: event.title,
            text: event.text,
            event_datetime: new Date(
              addSecondsToDateTime(event.event_datetime)
            ),
            is_released: event.is_released,
            media: event.media ?? [],
            use_ttq: event.use_ttq ?? false,
            // Only this save carries the audience. Release and unrelease omit
            // it, and the server leaves an absent audience untouched.
            affects_all_players: event.affects_all_players ?? true,
            assigned_player_ids:
              event.assigned_player_ids ?? event.assigned_players ?? [],
            affected_team_ids:
              event.affected_team_ids ?? event.affected_teams ?? [],
          }))

          dispatch(
            updateRoomDetails({
              roomId,
              scenario_title: values.scenarioTitle,
              events: eventsWithSeconds,
              ends_in: currentRoom?.ends_in,
              start_time: currentRoom?.start_time
                ? new Date(currentRoom.start_time)
                : undefined,
              status: currentRoom?.status!,
            })
          )
            .unwrap()
            .then(() => {
              dispatch(getRoomDetails(roomId))
              toast.success('Scenario saved successfully!')
            })
            .catch((err) => {
              toast.error(
                `Error saving scenario: ${err.message || 'Unknown error'}`
              )
            })
            .finally(() => {
              setIsSubmitting(false)
            })
        }}
        enableReinitialize
      >
        {({ values, setFieldValue }) => {
          const handleReleaseEvent = async (eventId: number) => {
            setReleasingEventId(eventId)
            try {
              const eventIndex = values.events.findIndex(
                (event) =>
                  (event.id || values.events.indexOf(event)) === eventId
              )

              if (eventIndex !== -1) {
                const updatedEvents = [...values.events]
                updatedEvents[eventIndex] = {
                  ...updatedEvents[eventIndex],
                  is_released: true,
                }
                setFieldValue('events', updatedEvents)

                const eventsWithSeconds = updatedEvents.map((event) => ({
                  id: event.id,
                  title: event.title,
                  text: event.text,
                  event_datetime: new Date(
                    addSecondsToDateTime(event.event_datetime)
                  ),
                  is_released: event.is_released,
                  media: event.media ?? [],
                }))

                await dispatch(
                  updateRoomDetails({
                    roomId,
                    scenario_title: values.scenarioTitle,
                    events: eventsWithSeconds,
                    ends_in: currentRoom?.ends_in,
                    start_time: currentRoom?.start_time
                      ? new Date(currentRoom.start_time)
                      : undefined,
                    status: currentRoom?.status!,
                  })
                ).unwrap()

                toast.success('Event released successfully!')
              }
            } catch (error) {
              toast.error('Failed to release event')
              throw error
            } finally {
              setReleasingEventId(null)
            }
          }
          const handleUnreleaseEvent = async (eventId: number) => {
            setUnreleasingEventId(eventId)
            try {
              const eventIndex = values.events.findIndex(
                (event) =>
                  (event.id || values.events.indexOf(event)) === eventId
              )

              if (eventIndex !== -1) {
                const updatedEvents = [...values.events]
                updatedEvents[eventIndex] = {
                  ...updatedEvents[eventIndex],
                  is_released: false,
                }
                setFieldValue('events', updatedEvents)

                const eventsWithSeconds = updatedEvents.map((event) => ({
                  id: event.id,
                  title: event.title,
                  text: event.text,
                  event_datetime: new Date(
                    addSecondsToDateTime(event.event_datetime)
                  ),
                  is_released: event.is_released,
                  media: event.media ?? [],
                }))

                await dispatch(
                  updateRoomDetails({
                    roomId,
                    scenario_title: values.scenarioTitle,
                    events: eventsWithSeconds,
                    ends_in: currentRoom?.ends_in,
                    start_time: currentRoom?.start_time
                      ? new Date(currentRoom.start_time)
                      : undefined,
                    status: currentRoom?.status!,
                  })
                ).unwrap()

                toast.success('Event unreleased successfully!')
              }
            } catch (error) {
              toast.error('Failed to unrelease event')
              throw error
            } finally {
              setUnreleasingEventId(null)
            }
          }

          return (
            <Form className='space-y-6'>
              <div>
                <label className='block text-sm font-medium text-dark-blue'>
                  Scenario Title
                </label>
                <Field
                  as={Input}
                  name='scenarioTitle'
                  placeholder='Enter scenario title'
                  className='mt-1'
                  disabled={isGameCompleted}
                />
                <ErrorMessage
                  name='scenarioTitle'
                  component='div'
                  className='mt-1 text-sm text-red-500'
                />
              </div>

              <div className='space-y-4'>
                {values.events.map((event, index) => (
                  <EventCard
                    key={event.id || index}
                    event={event}
                    index={index}
                    isGameCompleted={isGameCompleted}
                    isGameInProgress={isGameInProgress}
                    onDelete={(eventIndex: number) => {
                      if (eventIndex > 0) {
                        const newEvents = values.events.filter(
                          (_, i) => i !== eventIndex
                        )
                        setFieldValue('events', newEvents)
                      }
                    }}
                    onRelease={handleReleaseEvent}
                    isReleasing={releasingEventId === (event.id || index)}
                    onUnrelease={handleUnreleaseEvent}
                    isUnreleasing={unreleasingEventId === (event.id || index)}
                    roomId={roomId}
                    scenarioTitle={values.scenarioTitle ?? ''}
                    onMediaAccept={() => {}}
                  />
                ))}
              </div>

              {!isGameCompleted && (
                <div className='flex gap-2 justify-start'>
                  <Button
                    type='button'
                    onClick={() => {
                      const lastEventDateTime =
                        values.events.length > 0
                          ? values.events[values.events.length - 1]
                              .event_datetime
                          : getCurrentLocalDateTime()
                      setFieldValue('events', [
                        ...values.events,
                        {
                          id: Date.now(),
                          title: DEFAULT_VALUES.eventTitle,
                          text: DEFAULT_VALUES.eventText,
                          event_datetime: lastEventDateTime,
                          media: [],
                        },
                      ])
                    }}
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={isSubmitting}
                  >
                    <PlusIcon className='mr-2 w-5 h-5' /> Add Event
                  </Button>
                  <Button
                    type='submit'
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Scenario'}
                  </Button>
                </div>
              )}
            </Form>
          )
        }}
      </Formik>
      {importScenarioOpen && roomId && (
        <ImportModal
          key={`${roomId}-scenario-import`}
          isOpen={importScenarioOpen}
          onClose={() => setImportScenarioOpen(false)}
          scope='scenario_events'
          roomId={roomId}
          roomName={currentRoom.roomname?.toString()}
          onImportComplete={() => {
            setImportScenarioOpen(false)
            void dispatch(getRoomDetails(roomId))
          }}
        />
      )}
      </div>
    </TooltipProvider>
  )
}
